import { AHText, Box, useTime } from "arkh";
import React, { useLayoutEffect, useState } from "react";

import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import { UserLoggedInNavigatorScreenProps } from "../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { FormattedMessage } from "react-intl";
import { WebDateSelector } from "../../../../components/mobile/WebDateSelector/WebDateSelector";
import { useWorkspaceTimeZone } from "../../../../OS/WorkspaceTimezone";
import { DateTime } from "luxon";
import { OrgButton } from "../../OrgButton";

import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { WebModalHeader } from "../../WorkspaceTodos/WorkspaceTodoEdit/WorkspaceTodoEditMain/WebModalHeader";
import { WorkspaceSignalScheduleQuery } from "./__generated__/WorkspaceSignalScheduleQuery.graphql";
import { WorkspaceSignalSchedulePublishMutation } from "./__generated__/WorkspaceSignalSchedulePublishMutation.graphql";
import { RadioButton } from "../../WorkspaceTodoSignalShared/RadioButton";
import { WorkspaceScheduleArchiveDate } from "../../WorkspaceTodoSignalShared/WorkpaceScheduleArchiveDate";

function InnerWorkspaceSignalSchedule(
  props: UserLoggedInNavigatorScreenProps<"WorkspaceSignalSchedule"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WorkspaceSignalScheduleQuery>(
    graphql`
      query WorkspaceSignalScheduleQuery(
        $workspaceSignalId: ID!
        $workspaceId: ID!
      ) {
        workspace(id: $workspaceId) @required(action: THROW) {
          signal(id: $workspaceSignalId) @required(action: THROW) {
            id
            updatedAtDate {
              ...FormattedDate
              isoString
            }
            receiverStores {
              id
            }
            expireAtDate {
              isoString
            }
          }
        }
      }
    `,
    {
      workspaceSignalId: props.route.params.workspaceSignalId,
      workspaceId: props.route.params.workspaceId,
    },
    {
      fetchPolicy: "store-or-network",
      fetchKey: props.fetchKey,
    }
  );

  useLayoutEffect(() => {
    props.navigation.setOptions({
      header: () => (
        <WebModalHeader
          title={
            <>
              <WebModalHeader.Title>
                <FormattedMessage
                  defaultMessage="Publish Signal"
                  description="Workspace publish signal screen title"
                />
              </WebModalHeader.Title>
            </>
          }
          close={
            <>
              <WebModalHeader.CloseButton onPress={props.navigation.goBack} />
            </>
          }
        />
      ),
    });
  }, [props.navigation, props.route.params.workspaceId]);

  const [mode, setMode] = useState<"SCHEDULE" | "PUBLISH">("SCHEDULE");
  const [archiveDate, setArchiveDate] = useState<DateTime | null>(null);

  const now = useTime();

  return (
    <Box style={{ flex: 1 }}>
      <Box color="background" cornerRadius="large" style={{ flex: 1 }}>
        <Box space="large" spaceBottom="large" style={{ flex: 1 }}>
          <Box
            style={{ flexDirection: "row", justifyContent: "space-between" }}
          >
            <Box space="medium" style={{ flex: 1 }}>
              <RadioButton
                selected={mode === "SCHEDULE"}
                icon="schedule"
                onPress={() => {
                  setMode("SCHEDULE");
                }}
              >
                <FormattedMessage
                  defaultMessage="Schedule"
                  description="Workspace Signal Schedule button"
                />
              </RadioButton>
            </Box>
            <Box space="medium" style={{ flex: 1 }}>
              <RadioButton
                icon="send"
                selected={mode === "PUBLISH"}
                onPress={() => {
                  setMode("PUBLISH");
                }}
              >
                <FormattedMessage
                  defaultMessage="Publish Now"
                  description="Workspace Signal Schedule button"
                />
              </RadioButton>
            </Box>
          </Box>
          <Box space="medium" style={{ flex: 1 }}>
            {mode === "PUBLISH" ? (
              <PublishNow
                numberOfStores={data.workspace.signal.receiverStores.length}
                id={props.route.params.workspaceSignalId}
                onCompleted={() => {
                  props.navigation.popToTop();
                }}
                archiveDate={archiveDate}
                setArchiveDate={setArchiveDate}
              />
            ) : (
              <Schedule
                id={props.route.params.workspaceSignalId}
                numberOfStores={data.workspace.signal.receiverStores.length}
                minDate={now.plus({
                  minute: 10,
                })}
                maxDate={archiveDate ?? undefined}
                onCompleted={() => {
                  props.navigation.pop();
                }}
                archiveDate={archiveDate}
                setArchiveDate={setArchiveDate}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function Schedule(props: {
  id: string;
  onCompleted: () => void;
  numberOfStores: number;
  minDate: DateTime;
  maxDate?: DateTime;
  archiveDate: DateTime | null;
  setArchiveDate: (date: DateTime | null) => void;
}) {
  const [setPublishAt, isInFlight] = useMutation(graphql`
    mutation WorkspaceSignalScheduleSetPublishAtMutation(
      $id: ID!
      $publishAt: String
      $archiveAt: String
    ) @raw_response_type {
      signalWorkspace(id: $id) {
        updateScheduledPublishAt(
          newPublishAt: $publishAt
          newArchiveAt: $archiveAt
        ) {
          id

          scheduledPublishAt {
            isoString
            ...FormattedDate
          }
        }
      }
    }
  `);

  const tz = useWorkspaceTimeZone();
  const [date, setDate] = useState<DateTime | null>(null);
  const now = useTime();
  const intl_tokens = useIntlTokens();
  return (
    <Box style={{ flex: 1 }}>
      <Box style={{ flex: 1 }}>
        <AHText type="subtitle">
          <FormattedMessage
            description="WorkspaceSignalEditMain Date Title"
            defaultMessage="Publication date"
          />
        </AHText>

        <Box spaceVertical="small" style={{ flexDirection: "row" }}>
          <WebDateSelector
            type="datetime"
            timezone={tz}
            placeholder={
              <FormattedMessage
                description="WorkspaceSignalEditMain Date Placeholder"
                defaultMessage="add schedule"
              />
            }
            minDate={props.minDate}
            maxDate={props.maxDate}
            value={date}
            onChangeValue={(val) => {
              setDate(val);
            }}
          />
        </Box>
        <Box spaceTop="medium">
          <WorkspaceScheduleArchiveDate
            minDate={date ?? now}
            archiveDate={props.archiveDate}
            onChangeArchiveDate={props.setArchiveDate}
          />
        </Box>
      </Box>
      <AHText type="body" color="text-primary">
        <FormattedMessage
          defaultMessage="Your signal will be scheduled and published to selected units at the chosen time setting.{br}Please make sure all the details are correct before you continue."
          description="WorkspaceSignalSchedule: publish signal dialog message."
          values={{
            br: "\n",
          }}
        />
      </AHText>

      <Box
        style={{ flexDirection: "row", justifyContent: "flex-end" }}
        spaceTop="large"
      >
        <Box spaceRight="small">
          <OrgButton
            backgroundColor="neutral"
            tintColor="text-inverted"
            onPress={props.onCompleted}
            title={intl_tokens.Cancel}
          />
        </Box>
        <OrgButton
          backgroundColor="primary"
          tintColor="text-inverted"
          loading={isInFlight}
          disabled={!date}
          onPress={() => {
            setPublishAt({
              variables: {
                id: props.id,
                publishAt: date?.toISO() ?? null,
                archiveAt: props.archiveDate?.toISO() ?? null,
              },
              onCompleted: props.onCompleted,
            });
          }}
          title={
            <FormattedMessage
              description="WorkspaceSignalSchedule: publish signal dialog button"
              values={{
                storeCount: props.numberOfStores,
              }}
              defaultMessage="Schedule signal to {storeCount, plural, one {1 unit} other {# units}}"
            />
          }
        />
      </Box>
    </Box>
  );
}

function PublishNow(props: {
  id: string;
  onCompleted: () => void;
  numberOfStores: number;
  archiveDate: DateTime | null;
  setArchiveDate: (date: DateTime | null) => void;
}) {
  const now = useTime();
  const intl_tokens = useIntlTokens();
  return (
    <Box style={{ flex: 1, justifyContent: "space-between" }}>
      <Box
        style={{
          flex: 1,
        }}
      >
        <WorkspaceScheduleArchiveDate
          minDate={now}
          archiveDate={props.archiveDate}
          onChangeArchiveDate={props.setArchiveDate}
        />
      </Box>
      <Box
        style={{
          flexDirection: "column",
          justifyContent: "flex-end",
          flex: 1,
        }}
      >
        <AHText type="body" color="text-primary">
          <FormattedMessage
            values={{ br: "\n" }}
            defaultMessage="Your signal will be published directly to selected units.{br}Please make sure all the details are correct before you continue."
            description="WorkspaceSignalSchedule: publish now signal dialog message."
          />
        </AHText>
        <Box
          spaceTop="large"
          style={{
            flexDirection: "row",
            justifyContent: "flex-end",
          }}
        >
          <Box spaceRight="small">
            <OrgButton
              backgroundColor="neutral"
              tintColor="text-inverted"
              onPress={props.onCompleted}
              title={intl_tokens.Cancel}
            />
          </Box>
          <WorkspaceSignalEditMainPublishButton
            onCompleted={props.onCompleted}
            numberOfStores={props.numberOfStores}
            signalId={props.id}
            archiveDate={props.archiveDate}
          />
        </Box>
      </Box>
    </Box>
  );
}

function WorkspaceSignalEditMainPublishButton(props: {
  signalId: string;
  onCompleted: () => void;
  numberOfStores: number;
  archiveDate: DateTime | null;
}) {
  const [publish, isPublishing] =
    useMutation<WorkspaceSignalSchedulePublishMutation>(graphql`
      mutation WorkspaceSignalSchedulePublishMutation(
        $id: ID!
        $archiveAt: String
      ) {
        signalWorkspace(id: $id) {
          publish(archiveAt: $archiveAt) {
            status
          }
        }
      }
    `);

  const onPress = () => {
    publish({
      onCompleted() {
        props.onCompleted();
      },
      variables: {
        archiveAt: props.archiveDate?.toISO() ?? undefined,
        id: props.signalId,
      },
    });
  };
  return (
    <OrgButton
      backgroundColor="primary"
      tintColor="text-inverted"
      title={
        <FormattedMessage
          description="Publish signal dialog button"
          values={{
            storeCount: props.numberOfStores,
          }}
          defaultMessage="Publish to {storeCount, plural, one {1 unit} other {# units}}"
        />
      }
      onPress={onPress}
      loading={isPublishing}
    />
  );
}

export function WorkspaceSignalScheduleC(
  props: UserLoggedInNavigatorScreenProps<"WorkspaceSignalSchedule">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWorkspaceSignalSchedule fetchKey={fetchKey} {...props} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

export const WorkspaceSignalSchedule = withWebModalStack(
  WorkspaceSignalScheduleC,
  true,
  {
    height: 550,
    width: 600,
  }
);
